import type { Account, Budget, Plan, Transaction } from '../types';
import { buildBudgetUsage, type BudgetUsage } from './budgets';
import { addDays, normalizeDateISO, todayISO } from './date';

type TrendRow = { date: string; expense: number; income: number; investment: number };

export type DashboardAccountSummary = Account & {
  periodCredit: number;
  periodDebit: number;
  periodNet: number;
  isNegative: boolean;
};

export type DashboardCardSummary = Account & {
  outstanding: number;
  availableCredit: number;
  utilizationPct: number;
  utilizationState: 'normal' | 'elevated' | 'high' | 'over_limit';
};

export type DashboardAttentionItem = {
  id: string;
  label: string;
  value: string;
  reason: string;
  action: string;
  tone: 'info' | 'warning' | 'danger';
};

export type DashboardModel = {
  periodLabel: string;
  income: number;
  expense: number;
  investment: number;
  netCashFlow: number;
  totalAvailableBalance: number;
  liquidAccounts: DashboardAccountSummary[];
  creditCards: DashboardCardSummary[];
  creditOutstanding: number;
  availableCredit: number;
  upcoming: Plan[];
  upcomingTotal: number;
  overdue: Plan[];
  overdueTotal: number;
  nearestDueDate: string;
  topCategories: Array<{ category: string; amount: number; pct: number }>;
  budgetUsage: BudgetUsage[];
  budgetTotalLimit: number;
  budgetTotalSpent: number;
  budgetUsedPct: number;
  attention: DashboardAttentionItem[];
  recentTransactions: Transaction[];
  trend: TrendRow[];
  insights: string[];
};

const moneyCompact = (amount: number) => `₹${Math.round(amount).toLocaleString('en-IN')}`;

const sortByDateDesc = (a: Pick<Transaction, 'dateISO' | 'date' | 'id' | 'createdAt'>, b: Pick<Transaction, 'dateISO' | 'date' | 'id' | 'createdAt'>) => {
  const dateCompare = normalizeDateISO(b.dateISO || b.date).localeCompare(normalizeDateISO(a.dateISO || a.date));
  if (dateCompare) return dateCompare;
  return String(b.createdAt || b.id || '').localeCompare(String(a.createdAt || a.id || ''));
};

export const buildDashboardTrend = (rows: Transaction[]) => {
  const map: Record<string, TrendRow> = {};
  rows.forEach((row) => {
    const date = normalizeDateISO(row.dateISO || row.date);
    map[date] ||= { date: date.slice(5), expense: 0, income: 0, investment: 0 };
    if (row.type === 'Expense') map[date].expense += Number(row.amount || 0);
    if (row.type === 'Income') map[date].income += Number(row.amount || 0);
    if (row.type === 'Investment') map[date].investment += Number(row.amount || 0);
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

export function buildDashboardModel(input: {
  transactions: Transaction[];
  plans: Plan[];
  budgets: Budget[];
  accounts: Account[];
  today?: string;
  periodLabel?: string;
}): DashboardModel {
  const today = normalizeDateISO(input.today || todayISO());
  const rows = input.transactions.filter((row) => row.status !== 'deleted' && row.active !== false);
  const income = rows.filter((row) => row.type === 'Income').reduce((total, row) => total + Number(row.amount || 0), 0);
  const expense = rows.filter((row) => row.type === 'Expense').reduce((total, row) => total + Number(row.amount || 0), 0);
  const investment = rows.filter((row) => row.type === 'Investment').reduce((total, row) => total + Number(row.amount || 0), 0);
  const netCashFlow = income - expense - investment;
  const activeAccounts = input.accounts.filter((account) => account.status === 'active' && account.active !== false);
  const liquidAccounts = activeAccounts
    .filter((account) => account.type !== 'credit_card')
    .map((account) => {
      const accountRows = rows.filter((row) => row.accountId === account.id);
      const periodCredit = accountRows.filter((row) => row.type === 'Income').reduce((total, row) => total + Number(row.amount || 0), 0);
      const periodDebit = accountRows.filter((row) => row.type === 'Expense' || row.type === 'Investment').reduce((total, row) => total + Number(row.amount || 0), 0);
      return { ...account, periodCredit, periodDebit, periodNet: periodCredit - periodDebit, isNegative: Number(account.currentBalance || 0) < 0 };
    })
    .sort((a, b) => Number(b.currentBalance || 0) - Number(a.currentBalance || 0) || a.name.localeCompare(b.name));
  const creditCards = activeAccounts
    .filter((account) => account.type === 'credit_card')
    .map((account) => {
      const outstanding = Math.max(Number(account.currentBalance || 0), 0);
      const creditLimit = Number(account.creditLimit || 0);
      const utilizationPct = creditLimit > 0 ? Math.round((outstanding / creditLimit) * 100) : 0;
      const utilizationState: DashboardCardSummary['utilizationState'] = creditLimit > 0 && outstanding > creditLimit ? 'over_limit' : utilizationPct >= 80 ? 'high' : utilizationPct >= 50 ? 'elevated' : 'normal';
      return { ...account, outstanding, availableCredit: Number(account.availableLimit || Math.max(creditLimit - outstanding, 0)), utilizationPct, utilizationState };
    })
    .sort((a, b) => b.outstanding - a.outstanding || a.name.localeCompare(b.name));
  const totalAvailableBalance = liquidAccounts.reduce((total, account) => total + Number(account.currentBalance || 0), 0);
  const creditOutstanding = creditCards.reduce((total, card) => total + card.outstanding, 0);
  const availableCredit = creditCards.reduce((total, card) => total + card.availableCredit, 0);
  const activePlans = input.plans.filter((plan) => plan.status === 'active' || plan.status === 'Active');
  const upcoming = activePlans
    .filter((plan) => normalizeDateISO(plan.nextDueDate || plan.dueDate) >= today && normalizeDateISO(plan.nextDueDate || plan.dueDate) <= addDays(today, 15))
    .sort((a, b) => normalizeDateISO(a.nextDueDate || a.dueDate).localeCompare(normalizeDateISO(b.nextDueDate || b.dueDate)));
  const overdue = activePlans
    .filter((plan) => normalizeDateISO(plan.nextDueDate || plan.dueDate) < today)
    .sort((a, b) => normalizeDateISO(a.nextDueDate || a.dueDate).localeCompare(normalizeDateISO(b.nextDueDate || b.dueDate)));
  const upcomingTotal = upcoming.reduce((total, plan) => total + Number(plan.amount || 0), 0);
  const overdueTotal = overdue.reduce((total, plan) => total + Number(plan.amount || 0), 0);
  const nearestDueDate = upcoming[0] ? normalizeDateISO(upcoming[0].nextDueDate || upcoming[0].dueDate) : '';
  const expenseByCategory = rows
    .filter((row) => row.type === 'Expense')
    .reduce<Record<string, number>>((map, row) => {
      const key = row.category || 'Other';
      map[key] = (map[key] || 0) + Number(row.amount || 0);
      return map;
    }, {});
  const topCategories = Object.entries(expenseByCategory)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 5)
    .map(([category, amount]) => ({ category, amount, pct: expense > 0 ? Math.round((amount / expense) * 100) : 0 }));
  const budgetUsage = buildBudgetUsage(input.budgets, rows).sort((a, b) => b.usagePct - a.usagePct || a.category.localeCompare(b.category));
  const budgetTotalLimit = budgetUsage.reduce((total, budget) => total + Number(budget.limitAmount || 0), 0);
  const budgetTotalSpent = budgetUsage.reduce((total, budget) => total + Number(budget.spent || 0), 0);
  const budgetUsedPct = budgetTotalLimit > 0 ? Math.round((budgetTotalSpent / budgetTotalLimit) * 100) : 0;
  const unassignedCount = rows.filter((row) => !row.accountId).length;
  const plansMissingAccount = activePlans.filter((plan) => !plan.paidFromAccountId).length;
  const attention: DashboardAttentionItem[] = [
    ...(overdue.length ? [{ id: 'overdue', label: 'Overdue payments', value: `${moneyCompact(overdueTotal)} across ${overdue.length}`, reason: 'Payment dates are before today.', action: 'View plans', tone: 'danger' as const }] : []),
    ...(upcoming.length ? [{ id: 'upcoming', label: 'Upcoming in 15 days', value: `${moneyCompact(upcomingTotal)} across ${upcoming.length}`, reason: nearestDueDate ? `Nearest due ${nearestDueDate}` : 'Due soon.', action: 'View plans', tone: 'warning' as const }] : []),
    ...(creditOutstanding > 0 ? [{ id: 'credit-outstanding', label: 'Credit-card outstanding', value: moneyCompact(creditOutstanding), reason: 'Outstanding card balance needs monitoring.', action: 'Review cards', tone: creditCards.some((card) => card.utilizationState === 'high' || card.utilizationState === 'over_limit') ? 'danger' as const : 'warning' as const }] : []),
    ...liquidAccounts.filter((account) => account.isNegative).map((account) => ({ id: `negative-${account.id}`, label: `${account.name} negative`, value: moneyCompact(account.currentBalance), reason: 'Account balance is below zero.', action: 'Open statement', tone: 'danger' as const })),
    ...budgetUsage.filter((budget) => budget.state === 'crossed').slice(0, 3).map((budget) => ({ id: `budget-${budget.id}`, label: `${budget.category} budget exceeded`, value: `${moneyCompact(Math.abs(budget.remaining))} over`, reason: `${budget.usagePct}% of monthly limit used.`, action: 'Review transactions', tone: 'danger' as const })),
    ...budgetUsage.filter((budget) => budget.state === 'warning').slice(0, 2).map((budget) => ({ id: `budget-warn-${budget.id}`, label: `${budget.category} budget nearing limit`, value: `${budget.usagePct}% used`, reason: 'Budget is above the warning threshold.', action: 'Review budget', tone: 'warning' as const })),
    ...(unassignedCount ? [{ id: 'unassigned', label: 'Unassigned transactions', value: `${unassignedCount} item(s)`, reason: 'Account is missing, so account-level statements may be incomplete.', action: 'Review transactions', tone: 'info' as const }] : []),
    ...(plansMissingAccount ? [{ id: 'plan-account', label: 'Plans missing account', value: `${plansMissingAccount} plan(s)`, reason: 'Mark paid will not deduct an account unless one is selected.', action: 'Update plans', tone: 'info' as const }] : [])
  ].slice(0, 6);
  const recentTransactions = [...rows].sort(sortByDateDesc).slice(0, 8);
  const trend = buildDashboardTrend(rows);
  const dailyPeak = trend.reduce<TrendRow | null>((peak, row) => !peak || row.expense > peak.expense ? row : peak, null);
  const insights = [
    expense > income ? `Expenses are ${moneyCompact(expense - income)} higher than income this period.` : income || expense ? `Income covers expenses by ${moneyCompact(income - expense)} before investments.` : '',
    topCategories[0] ? `${topCategories[0].category} accounts for ${topCategories[0].pct}% of spending.` : '',
    dailyPeak && dailyPeak.expense > 0 ? `Daily spending peaked on ${dailyPeak.date} at ${moneyCompact(dailyPeak.expense)}.` : '',
    !rows.length ? 'No transactions in this period yet.' : ''
  ].filter(Boolean);
  return {
    periodLabel: input.periodLabel || 'This Month',
    income,
    expense,
    investment,
    netCashFlow,
    totalAvailableBalance,
    liquidAccounts,
    creditCards,
    creditOutstanding,
    availableCredit,
    upcoming,
    upcomingTotal,
    overdue,
    overdueTotal,
    nearestDueDate,
    topCategories,
    budgetUsage,
    budgetTotalLimit,
    budgetTotalSpent,
    budgetUsedPct,
    attention,
    recentTransactions,
    trend,
    insights
  };
}
